import { Link, useLocation } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useTenant, useUnitConversion } from '@providers/TenantProvider';
import { useSelectedProductionLine } from '@stores/appStore';
import { ProductionLineSelector } from './ProductionLineSelector';
import { cn } from '@lib/utils';

interface NavbarProps {
  isSignedOutPage?: boolean;
}

export function Navbar({ isSignedOutPage = false }: NavbarProps) {
  const location = useLocation();
  const { config } = useTenant();
  const { setUnitSystem, isMetric } = useUnitConversion();
  useSelectedProductionLine();

  const navLinks = [
    { href: '/dashboard', label: 'Runs', show: config.routes.dashboard },
    { href: '/rca', label: 'Root Cause Analysis', show: config.routes.cipAnalytics },
    { href: '/weeklyTrend', label: 'Weekly Trend', show: config.routes.weeklyTrend },
  ];

  const isActiveLink = (href: string) => location.pathname === href;

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center px-4 w-full">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2">
          <img
            src={config.branding.logo}
            alt={config.branding.companyName}
            className="h-8 w-auto"
          />
        </Link>

        {/* Navigation Links */}
        {!isSignedOutPage && (
          <nav className="flex items-center space-x-6 text-sm font-medium ml-6">
            {navLinks
              .filter((link) => link.show)
              .map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'transition-colors hover:text-foreground/80',
                    isActiveLink(link.href)
                      ? 'text-foreground font-semibold'
                      : 'text-foreground/60'
                  )}
                >
                  {link.label}
                </Link>
              ))}
          </nav>
        )}

        {/* Right side controls */}
        <div className="ml-auto flex items-center space-x-4">
          {!isSignedOutPage && (
            <>
              {/* Production Line Selector */}
              <ProductionLineSelector />

              {/* Unit System Toggle */}
              <div className="flex items-center rounded-md border bg-muted p-1">
                <button
                  onClick={() => setUnitSystem('metric')}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded transition-colors',
                    isMetric
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted-foreground/10'
                  )}
                >
                  Metric
                </button>
                <button
                  onClick={() => setUnitSystem('imperial')}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded transition-colors',
                    !isMetric
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted-foreground/10'
                  )}
                >
                  Imperial
                </button>
              </div>

              {/* Logout button */}
              <Link
                to="/signed-out?logout=true"
                className="flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground"
              >
                <LogOut className="h-4 w-4" />
                <span className="sr-only">Logout</span>
              </Link>
            </>
          )}

          {isSignedOutPage && (
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Log In
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

export default Navbar;
